import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { withStyles, FormControl, Typography } from '@material-ui/core';
import { CloudUploadOutlined, CloudDoneOutlined } from '@material-ui/icons';
import { Storage } from 'aws-amplify';

import { useTranslation } from 'react-i18next';

import { CONSTANTS } from '../../constants/constants';
import AlertDialog from '../UI/AlertDialog';

const styles = theme => ({
  dropArea: {
    width: '100%',
    borderWidth: 2,
    borderStyle: 'dashed',
    borderColor: theme.palette.primary.main,
    marginTop: 16,
    padding: 16,
    marginBottom: 8,
    textAlign: 'center'
  },
  dragIn: {
    opacity: 0.5
  },
  uploadBtn: {
    fontSize: '4em'
  },
  searchLabel: {
    cursor: 'pointer',
    color: theme.palette.primary.main,
    display: 'inline-block'
  },
  uploadedIcon: {
    opacity: 0.7,
    fontSize: '2.5em',
    marginRight: 16,
    verticalAlign: 'middle'
  },
  formControl: {
    position: 'relative',
    width: '100%',
    marginTop: 16,
    marginBottom: 8
  },
  hiddenFileInput: {
    opacity: 0,
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)'
  }
});

const DragAndDropArea = (props) => {
  const [dragging, setDragging] = useState(false);
  const [files, setFiles] = useState([]);
  const [error, setError] = useState(null);

  const { classes, accept, multiple, returnFormat, type, currentFiles = [] } = props;
  const dropRef = React.createRef();
  const { t } = useTranslation();
  const extensionsAllowed = accept.split(',');

  useEffect(() => {
    const div = dropRef.current;

    div.addEventListener('dragenter', handleDragIn);
    div.addEventListener('dragover', handleDragIn);
    div.addEventListener('dragleave', handleDragOut);
    div.addEventListener('dragend', handleDragOut);
    div.addEventListener('drop', handleDrop);

    return () => {
      div.removeEventListener('dragenter', handleDragIn);
      div.removeEventListener('dragover', handleDragIn);
      div.removeEventListener('dragleave', handleDragOut);
      div.removeEventListener('dragend', handleDragOut);
      div.removeEventListener('drop', handleDrop);
    };
  }, []);

  const newError = (typeError, msgError) => {
    const error = new Error(typeError);
    error.customMessage = msgError;
    throw error;
  };

  const checkFileSize = (size) => {
    if (size > CONSTANTS.DEFAULT_FILE_SIZE.MAX) {
      newError('Max file size excedded', t('docUpload:fileSizeExcceded'));
    }
  };

  const checkFileNameAndExtension = (name) => {
    const ext = /[.]\w{3,4}$/.exec(name)[0];

    if (!ext || !extensionsAllowed.includes(ext)) {
      newError('Wrong Extension', `${t('docUpdate:extensionError')} ${extensionsAllowed.join(', ')}`);
    }
  };

  const checkFiles = (files, currentFiles) => {
    const fileLimit = type === 'bundle' ? CONSTANTS.BUNDLE.FILES_LIMIT : CONSTANTS.DOCUMENT.FILES_LIMIT;
    if (files.length + currentFiles.length > fileLimit) {
      newError('Max file limit excedded', t('docUpload:fileLimitExcceded', { fileLimit }));
    }
    files.forEach(({ name }) => {
      checkFileNameAndExtension(name);
    });
  };

  const handleDragIn = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(true);
  };

  const handleDragOut = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    let files;
    try {
      if (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        files = Array.from(e.dataTransfer.files);
        document.getElementById('file-upload').files = e.dataTransfer.files;
      } else {
        files = Array.from(e.target.files);
      }

      let processedFiles = [];
      let size = 0;
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const execResult = /\.([^.]+)$/.exec(file.name);
        const ext = execResult && execResult[0];
        const fileName = file.name.replace(ext, ext.toLowerCase());
        let newFile = new File([file], fileName, { type: file.type });

        if (returnFormat === 'base64') {
          newFile = await convertToBase64(newFile);
        }

        processedFiles.push(newFile);
        size += file.size;
      }

      for (const file of currentFiles) {
        let key = file.path.replace(/[.]\w{3,4}$/, '.pdf');
        const response = await Storage.list(key);
        size += response[0].size;
      }

      checkFileSize(size);
      checkFiles(processedFiles, currentFiles);
      props.setContent(processedFiles);
      setFiles(processedFiles);
    } catch (e) {
      console.error('Error creating PDF', e);
      setError(e.customMessage || t('templateUploadView:genericError'));
    }
  };

  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = function () {
        const name = file.name;
        const base64 = reader.result.split('base64,').pop();
        resolve({ name, base64 });
      };
      reader.onerror = function (error) {
        reject(error);
      };
    });
  };

  return (
    <div style={{ width: '100%' }}>
      <FormControl className={classes.formControl}>
        <input
          type='file'
          name='file'
          accept={accept || '*'}
          className={classes.hiddenFileInput}
          id='file-upload'
          onChange={handleDrop}
          multiple={multiple}
          required
        />
        {files && files.map((file) => (
          <div key={file.name} id='uploaded-file' style={{ display: 'block' }}>
            <CloudDoneOutlined className={classes.uploadedIcon} />
            <span id='filename'>{file.name}</span>
          </div>
        ))}

        <div
          id='drop-area'
          className={classNames(classes.dropArea, {
            [classes.dragIn]: dragging
          })}
          ref={dropRef}
        >
          <CloudUploadOutlined className={classes.uploadBtn} color='primary' />
          <Typography>{t('dragAndDrop:dragFile')}</Typography>
          <Typography>{t('dragAndDrop:o')}</Typography>
          <label htmlFor='file-upload' className={classes.searchLabel}>{t('dragAndDrop:lookForOne')}</label>
        </div>
      </FormControl>
      <AlertDialog open={!!error} title='Error' description={error} setOpen={() => setError(null)} />
    </div>
  );
};

DragAndDropArea.propTypes = {
  classes: PropTypes.object.isRequired,
  accept: PropTypes.string,
  multiple: PropTypes.bool,
  setContent: PropTypes.func.isRequired,
  returnFormat: PropTypes.string
};

export default withStyles(styles, { withTheme: true })(DragAndDropArea);
